<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { $bus, mqtt } from '@/common/events'
import { dialog } from '@/common/dialog'
import { hcu } from '@/common/hcu'
import { useTranslate } from '@/composables/language'
import { useBarcode, onBarcodeStatus, onBarcodeReceived } from '@/composables/hardware/barcode'
import EventLog from '@/components/EventLog.vue'

const { t } = useTranslate();
const { barcode } = useBarcode();

const props = defineProps({
  device: {
    type: Object,
    default: null
  },
  user: {
    type: Object,
    default: null
  },
});

const emit = defineEmits([ "onLog" ]);
function insertLog(record) {
  emit("onLog", record);
}

const deviceStatus = ref(null);

const choice = ref('protocol');

function setChoice(value) {
  choice.value = value;
}

const lastCode = ref();

const logRecords = ref([
  // { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
  // { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
  // { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
  // { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
  // { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
  // { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
]);

const canResetLog = ref(true);

const openDelay = ref(null);
const placeholderHeight = ref(null);

const eventLogElement = ref();
const eventLogHeight = ref(300);

//Calculate event log height
function calculateEventLogHeight() {
  const minHeight = 150;
  const element = document.getElementById("eventLog");
  const offset = element.getBoundingClientRect(); //Get element position within the window
  eventLogHeight.value = Math.max(minHeight, window.innerHeight - offset.top - 20);
}

onMounted(() => {
  //initMQTT();
  calculateEventLogHeight();
});

onUnmounted(() => {
});

onBarcodeStatus((data) => {
  console.log("[barcode] onBarcodeStatus", data);
  let now = new Date();
  let time = now.toISOString().split("T")[1].substr(0, 8);
  logRecords.value.push({ text: "Status changed", time: time, type: "warning", direction: "inbound" });
});

onBarcodeReceived((data) => {
  console.log("[barcode] onBarcodeReceived", data);
  lastCode.value = data.code;
  let now = new Date();
  let time = now.toISOString().split("T")[1].substr(0, 8);
  logRecords.value.push({ text: data.code, time: time, type: "success", direction: "inbound" });
});

function clearLogs() {
  logRecords.value = [ ];
}

function appendLog(record) {
  logRecords.value.push(record);
}

function clearLog() {
  logRecords.value = [ ];
}
</script>

<template>
  <div class="relative dark:text-white">

    <div class="">
      <!-- Top right corner -->
      <div class="flex flex-row justify-between space-x-2 mt-4">

        <div class="grid place-items-center">
          <div class="whitespace-nowrap">
            {{ $t('service.barcodeReader') }}
            <span class="font-bold" v-if="props.device && props.device.model">{{ props.device.model }}</span>
            <!-- <span class="font-mono text-blue-500 ml-2" v-if="props.device?.model">{{ props.device.model }}</span> -->
            <!-- <span class="font-mono text-purple-500 ml-2" v-if="props.device?.model">{{ props.device.model }}</span> -->
          </div>
        </div>

        <!-- Action button -->
        <div class="flex flex-row justify-items-strech space-x-2">
          <span class="ml-1 mr-1 mt-2">
            <i class="fa fa-power-off text-yellow-300 mt-1 animate-bounce" v-if="deviceStatus == 'init' || deviceStatus == 'connecting'"></i>
            <i class="fa fa-power-off text-red-600 mt-1" v-else-if="deviceStatus == 'closed' || deviceStatus == 'error'"></i>
            <i class="fa fa-power-off text-green-400 mt-1" v-else-if="deviceStatus == 'open'"></i>
            <i class="fa fa-power-off text-slate-400 mt-1" v-else></i>
          </span>
          <Button type="secondary" @click="() => clearLog()">{{ $t('service.clearLog') }}</Button>
        </div>

      </div>
    </div>

    <!-- Content -->
    <div class="w-full">

      <!-- Last code -->
      <div class="mt-2 pb-2 w-full">
        <Label>{{ $t('admin.last_card') }}</Label>
        <TextBox v-model="lastCode" :placeholder="$t('generic.none')" :enabled="false" class="w-full mt-2" />
      </div>

      <!-- Logger -->
      <div class="w-full h-full">

        <div class="w-full border-b mb-4 dark:border-gray-700">
          <button class="choice-button" :class="{ 'choice-active': choice == 'protocol' }" @click="setChoice('protocol')">{{ $t('service.tab_protocol') }}</button>
          <button class="choice-button" :class="{ 'choice-active': choice == 'status' }" @click="setChoice('status')">{{ $t('service.tab_status') }}</button>
        </div>

        <EventLog id="eventLog" ref="eventLogElement" :records="logRecords" :style="'height: ' + eventLogHeight + 'px'" v-if="choice == 'protocol'">
          <template #empty>
            {{ $t('service.no_logs') }}
          </template>
        </EventLog>

        <pre class="px-3 py-3 mb-10 border rounded bg-amber-100 text-amber-700 text-xs font-mono" v-if="choice == 'status'">{{ props.device }}</pre>

      </div>

    </div>

  </div>
</template>

<style scoped>
a {
  outline: none;
}

.label {
  position: relative;
  top: 5px;
  height: 30px;
  line-height: 30px;
}

.choice-button {
  @apply px-5 py-3;
}

.choice-button.choice-active {
  border-bottom: 3px solid rgb(26 86 219);
}

.button-circle {
  @apply aspect-square p-4 font-medium border border-gray-200 rounded-lg shadow-md;
  /* @apply bg-white hover:bg-gray-100 */ /* White button */
  @apply text-white bg-blue-700 hover:bg-blue-800; /* Blue button */
  position: absolute;
  /* left: 260px; */
  /* top: 260px; */
  left: 165px;
  top: 165px;
  z-index: 10;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  font-size: 24px;
}

.button-circle.button-circle-static,
.button-circle.button-circle-static:hover {
  @apply bg-white border-gray-200 hover:bg-white hover:border-gray-200;
  cursor: default;
}

.button-circle.button-circle-inactive,
.button-circle.button-circle-inactive:hover {
  @apply bg-gray-100 text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
  cursor: default;
}

.button-door {
  @apply w-16;
  @apply flex justify-center aspect-square p-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.button-door-label {
  @apply pt-0 text-xl;
  @apply font-bold tracking-tight dark:text-white;
}

@media only screen and (max-width: 1024px) { 
  .button-door {
    @apply w-14 p-3;
  }

  .button-door-label {
    @apply text-lg;
  }
}

.button-door.button-door-selected {
  border: 2px solid rgba(26,86,219,1);
}

.button-door.button-door-position,
.button-door.button-door-position:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}
</style>
